"use client";

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import { Pagination } from "../../../components/admin/common/PaginationNumber";
import { MemberListTable } from "../../../components/admin/members/MemberListTable";
import { getMembersByStatus } from "../../../services/memberServices";
import ExportButtons from "../../../components/admin/common/ExportButtons";
import { getCookie } from "../../../utils";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";

const entriesOptions = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
];

const MembersListPage = () => {
  const [entriesOption, setEntriesOption] = useState(entriesOptions[0]);
  const [page, setPage] = useState(1);
  const [memberList, setMemberList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalDocs, setTotalDocs] = useState(0);
  const fixedStatus = "pending";

  useEffect(() => {
    const url = new URL(window.location.href);
    const urlPage = parseInt(url.searchParams.get("page") || "1", 10);
    setPage(urlPage > 0 ? urlPage : 1);
  }, []);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["members", page, entriesOption.value],
    queryFn: () => getMembersByStatus(fixedStatus, page, entriesOption.value),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.data?.members) {
      setMemberList(data.data.members);
      setTotalPages(data.data.totalPages || 1);
      setHasMore(data.data.hasMore || false);
      setTotalDocs(data.data.totalDocs || 0);
    }
  }, [data]);

  const handlePageChange = (pageNum) => {
    setPage(pageNum);

    //  Update URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(pageNum));
    window.history.pushState({}, "", url);

    //  Smooth scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setPage(1);

    //  Reset to page=1 in URL
    const url = new URL(window.location.href);
    url.searchParams.set("page", "1");
    window.history.pushState({}, "", url);
  };

  const startIndex = (page - 1) * entriesOption.value + 1;
  const endIndex = Math.min(page * entriesOption.value, totalDocs);

  const userId = getCookie("userId");

  const role = getCookie("role");

  const { data: permissionsData } = useUserModulePermissions(
    "Members",
    userId,
    {
      enabled: role === "maintainer" && userId && userId !== "undefined",
    }
  );
  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <div className="h-full">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4 overflow-y-auto">
              <Header
                title="Member Management"
                desc="Comprehensive overview of all organization members."
              />

              <div className="flex justify-between items-center mb-4">
                <DropdownSelect
                  label="Entries"
                  selected={entriesOption}
                  onChange={handleEntriesChange}
                  options={entriesOptions}
                />

                {!isLoading && !isError && totalDocs > 0 && (
                  <div className="text-sm text-gray-600">
                    Showing {startIndex} to {endIndex} of {totalDocs} entries
                  </div>
                )}
              </div>

              <div className="mb-4 -translate-y-3">
                <ExportButtons model="members" />
              </div>

              {isLoading || isFetching ? (
                <div className="flex justify-center items-center py-8">
                  <p>Loading members...</p>
                </div>
              ) : isError ? (
                <div className="flex justify-center items-center py-8">
                  <p className="text-red-500">Failed to fetch members.</p>
                </div>
              ) : memberList.length === 0 ? (
                <div className="flex justify-center items-center py-8">
                  <p className="text-gray-500">No members found.</p>
                </div>
              ) : (
                <>
                  <MemberListTable
                    members={memberList}
                    refetch={refetch}
                    startIndex={(page - 1) * entriesOption.value}
                    permissionsData={permissionsData?.data ?? null}
                  />

                  {totalPages > 1 && (
                    <div className="mt-6">
                      <Pagination
                        totalPages={totalPages}
                        currentPage={page}
                        onPageChange={handlePageChange}
                        showPages={5}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default MembersListPage;
